import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import {
	BackendTag,
	CreatedShare,
	ErrorResponse,
	Experience,
	ExperienceFetchTypes,
	ExperienceRequest,
	ExternalItem,
	Imagesizes,
	LoginInfoToBackend,
	LoginResponse,
	MpRegisterRequest,
	OrgRequest,
	OrgReturn,
	ResponseCreateInfo,
	ResponseClientRequest,
	ShareExperienceRequest,
	TagRequest,
	TokenPair,
	userReturn,
	UserSettings,
	UserUpdateRequest,
	VectorFileStatus,
} from '@meta-press/shared-plugins';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class BackendService {
	private backendUrl = environment.apiUrl;

	private ExperienceApiUrl = environment.ExperienceApiUrl;
	private ExperienceAnonymApi = environment.ExperienceAnonymApi;
	private ExternalApiUrl = environment.ExternalApiUrl;
	private ExternalShareApiUrl = environment.ExternalShareApiUrl;
	private UserManagementApiUrl = environment.UserManagementApiUrl;
	private OrgManagementApiUrl = environment.OrgManagementApiUrl;
	private userInfoApiUrl = environment.userInfoApiUrl;
	private TagManagementApiUrl = environment.TagManagementApiUrl;
	private FileShareApiUrl = environment.FileShareApiUrl;
	private SourceFetcherApiUrl = environment.FetchURLApiURL;
	private OpenAiUrl: string = environment.AIEndPoint;

	private accessToken?: string;
	private refreshToken?: string;
	private tokenExpiration?: number;

	constructor(private http: HttpClient) {}

	//storage
	//Post
	public SendFileToBackendService(
		file: File,
		meta: string,
	): Observable<ExternalItem> {
		const formData = new FormData();
		formData.append('file', file, file.name);
		formData.append('meta', meta);
		return this.http.post<ExternalItem>(
			this.backendUrl + this.ExternalApiUrl,
			formData,
		);
	}

	//getall
	public GetFiles(): Observable<ExternalItem[]> {
		return this.http.get<ExternalItem[]>(
			this.backendUrl + this.ExternalApiUrl,
		);
	}

	public GetResizedImage(Id: number, Size: Imagesizes): Observable<Blob> {
		return this.http.get(
			`${this.backendUrl + this.ExternalApiUrl}/image/${Id}?size=${Size}`,
			{
				responseType: 'blob',
			},
		);
	}

	//get single
	public GetSingleFile(id: number): Observable<Blob> {
		return this.http.get(`${this.backendUrl + this.ExternalApiUrl}/${id}`, {
			responseType: 'blob',
		});
	}
	//delete
	public DeleteFileFromServer(id: number): Observable<object> {
		return this.http.delete(
			`${this.backendUrl + this.ExternalApiUrl}/${id}`,
		);
	}

	//experiense
	//get
	public GetAllExperiences(
		getArchivedAlso: ExperienceFetchTypes = ExperienceFetchTypes.All,
	): Observable<Experience[]> {
		return this.http.get<Experience[]>(
			`${this.backendUrl + this.ExperienceApiUrl}?archived=${getArchivedAlso}`,
		);
	}

	//getSingle
	public GetSingleExperiences(id: number): Observable<string> {
		return this.http.get<string>(
			`${this.backendUrl + this.ExperienceApiUrl}/${id}`,
		);
	}

	public GetExperienceByUuid(uuid: string): Observable<Experience> {
		return this.http.get<Experience>(
			`${this.backendUrl + this.ExperienceAnonymApi}/${uuid}`,
		);
	}

	public AddNewExperience(
		name: string,
		Blob: string,
		version: number,
	): Observable<object> {
		return this.http.post(this.backendUrl + this.ExperienceApiUrl, {
			name: name,
			jsonBlob: Blob,
			version: version,
		});
	}

	public UpdateExperience(experience: ExperienceRequest): Observable<object> {
		return this.http.patch(
			this.backendUrl + this.ExperienceApiUrl,
			experience,
		);
	}

	public DeleteExperience(id: number): Observable<object> {
		return this.http.delete(
			`${this.backendUrl + this.ExperienceApiUrl}/${id}`,
		);
	}

	public DeleteAllExperience(): Observable<boolean> {
		// Get all experiences as list
		return this.GetAllExperiences().pipe(
			tap((experiences: Experience[]) => {
				// Delete each experience
				experiences.forEach((experience: Experience) => {
					this.DeleteExperience(experience.id).subscribe();
				});
			}),
			catchError((error) => {
				console.error('Error deleting experiences:', error);
				return throwError(
					() => new Error('Failed to delete experiences'),
				);
			}),
			map((experiences: Experience[]) => {
				return true;
			}),
		);
	}

	//experience share
	public ShareExperience(
		SharedExperience: ShareExperienceRequest,
	): Observable<CreatedShare | ErrorResponse> {
		return this.http.post<CreatedShare | ErrorResponse>(
			this.backendUrl + this.ExternalShareApiUrl,
			SharedExperience,
		);
	}

	public UpdateSharedExperience(
		uuid: string,
		sharedExperience: ShareExperienceRequest,
	): Observable<CreatedShare | ErrorResponse> {
		return this.http.patch<CreatedShare>(
			`${this.backendUrl + this.ExternalShareApiUrl}/update/${uuid}`,
			sharedExperience,
		);
	}

	public GetSharedExperiences(): Observable<CreatedShare[]> {
		return this.http.get<CreatedShare[]>(
			this.backendUrl + this.ExternalShareApiUrl,
		);
	}
	//public
	//uses same model as external item, so we reuse it here
	public GetSharedExperienceByUuid(uuid: string): Observable<ExternalItem> {
		return this.http.get<ExternalItem>(
			`${this.backendUrl + this.ExternalShareApiUrl}/${uuid}`,
		);
	}

	public UpdateShare(
		id: number,
		enabled: boolean,
		expirationDate: number,
	): Observable<string> {
		return this.http.patch<string>(
			`${this.backendUrl + this.ExternalShareApiUrl}/${id}?expirationDate=${expirationDate}`,
			enabled,
		);
	}

	public DeleteShare(id: number): Observable<object> {
		return this.http.delete(
			`${this.backendUrl + this.ExternalShareApiUrl}/${id}`,
		);
	}

	//user management
	public GetUser(id: string): Observable<userReturn> {
		return this.http.get<userReturn>(
			`${this.backendUrl + this.UserManagementApiUrl}/${id}`,
		);
	}

	public GetUsers(): Observable<userReturn[]> {
		return this.http.get<userReturn[]>(
			this.backendUrl + this.UserManagementApiUrl,
		);
	}

	public UpdateUserInfo(
		userId: string,
		requestData: UserUpdateRequest,
	): Observable<any> {
		return this.http.patch<any>(
			`${this.backendUrl + this.UserManagementApiUrl}?userId=${userId}`,
			requestData,
		);
	}

	public UpdateCurrentUserInfo(
		requestData: UserUpdateRequest,
	): Observable<any> {
		return this.http.patch<any>(
			`${this.backendUrl + this.UserManagementApiUrl}/self`,
			requestData,
		);
	}

	//adds user to org and sets role

	public AddUserToOrgAndRole(
		userId: string,
		updateRequest: any,
	): Observable<any> {
		return this.http.post<any>(
			`${this.backendUrl + this.UserManagementApiUrl}/organization/users/${userId}`,
			updateRequest,
		);
	}

	public AddNewUser(request: MpRegisterRequest): Observable<userReturn> {
		return this.http.post<userReturn>(
			this.backendUrl + this.UserManagementApiUrl,
			request,
		);
	}

	public AddNewUserAndSendInvite(
		request: MpRegisterRequest,
	): Observable<userReturn> {
		return this.http.post<userReturn>(
			`${this.backendUrl + this.UserManagementApiUrl}/SendByEmail`,
			request,
		);
	}

	public DeleteUser(id: string): Observable<object> {
		return this.http.delete(
			`${this.backendUrl + this.UserManagementApiUrl}/${id}`,
		);
	}

	//org
	public GetORgs(): Observable<OrgReturn[]> {
		return this.http.get<OrgReturn[]>(
			this.backendUrl + this.OrgManagementApiUrl,
		);
	}

	public GetORg(id: number): Observable<any> {
		return this.http.get<OrgReturn>(
			`${this.backendUrl + this.OrgManagementApiUrl}/${id}`,
		);
	}

	public CreateOrg(request: OrgRequest): Observable<number> {
		return this.http.post<number>(
			this.backendUrl + this.OrgManagementApiUrl,
			request,
		);
	}

	public UpdateOrg(request: OrgRequest): Observable<any> {
		return this.http.patch<any>(
			`${this.backendUrl + this.OrgManagementApiUrl}/${request.Id}`,
			request,
		);
	}

	public DeleteOrg(id: number): Observable<any> {
		return this.http.delete<any>(
			`${this.backendUrl + this.OrgManagementApiUrl}/${id}`,
		);
	}

	public GetCurrentUserInfo(): Observable<userReturn> {
		return this.http.get<userReturn>(this.backendUrl + this.userInfoApiUrl);
	}

	public UpdateSettings(settings: UserSettings): Observable<any> {
		return this.http.patch<any>(
			this.backendUrl + this.userInfoApiUrl,
			settings,
		);
	}

	public UpdateTargetSettings(
		settings: UserSettings,
		id: string,
	): Observable<any> {
		return this.http.patch<any>(
			`${this.backendUrl + this.userInfoApiUrl}/${id}`,
			settings,
		);
	}

	public GetImage(
		shareId: string,
		imageId: number,
		size: Imagesizes,
	): Observable<Blob> {
		return this.http.get<Blob>(
			`${this.backendUrl + this.FileShareApiUrl}/Image/${shareId}/${imageId}?size=${size}`,
		);
	}

	public GetFile(sharedId: string, fileId: number): Observable<Blob> {
		return this.http.get<Blob>(
			`${this.backendUrl + this.FileShareApiUrl}/File/${sharedId}/${fileId}`,
		);
	}

	public AddNewTag(tagInfo: TagRequest): Observable<number> {
		return this.http.post<number>(
			this.backendUrl + this.TagManagementApiUrl,
			tagInfo,
		);
	}

	public GetTags(): Observable<BackendTag[]> {
		return this.http.get<BackendTag[]>(
			this.backendUrl + this.TagManagementApiUrl,
		);
	}

	public GetTag(id: number): Observable<BackendTag> {
		return this.http.get<BackendTag>(
			this.backendUrl + this.TagManagementApiUrl + '/' + id,
		);
	}

	public UpdateTag(id: number, tagInfo: TagRequest): Observable<any> {
		return this.http.patch<any>(
			this.backendUrl + this.TagManagementApiUrl + '/' + id,
			tagInfo,
		);
	}

	public DeleteTag(id: number): Observable<any> {
		return this.http.delete<any>(
			`${this.backendUrl + this.TagManagementApiUrl}/${id}`,
		);
	}

	//tag adding
	public AddTagToArticle(tagId: number, articleId: number): Observable<any> {
		return this.http.post<any>(
			`${this.backendUrl + this.TagManagementApiUrl}/add/${tagId}/${articleId}`,
			null,
		);
	}

	public RemoveTagFromArticle(
		tagId: number,
		articleId: number,
	): Observable<any> {
		return this.http.delete<any>(
			`${this.backendUrl + this.TagManagementApiUrl}/add/${tagId}/${articleId}`,
		);
	}

	public GetCurrentUserToken(
		unixStartDate: number,
		unixEndDate: number,
	): Observable<number> {
		return this.http.get<number>(
			`${this.backendUrl + this.UserManagementApiUrl}/tokens?startDate=${unixStartDate}&endDate=${unixEndDate}`,
		);
	}

	public GetUserToken(
		id: string,
		unixStartDate: number,
		unixEndDate: number,
	): Observable<number> {
		return this.http.get<number>(
			`${this.backendUrl + this.UserManagementApiUrl}/${id}tokens?startDate=${unixStartDate}&endDate=${unixEndDate}`,
		);
	}

	//retuns -1 if there is no token tier
	public GetUserTokenQuota(id: string): Observable<number> {
		return this.http.get<number>(
			`${this.backendUrl + this.UserManagementApiUrl}/${id}/tokenUsage`,
		);
	}

	public GetOrgTokenUsage(
		id: number,
		unixStartDate: number,
		unixEndDate: number,
	): Observable<TokenPair> {
		return this.http.get<TokenPair>(
			`${this.backendUrl + this.UserManagementApiUrl}/${id}/tokens?startDate=${unixStartDate}&endDate=${unixEndDate}`,
		);
	}

	//user
	public Login(
		UserName: string,
		password: string,
		useCookie: boolean,
		useSession: boolean,
	): Observable<LoginResponse | undefined> {
		const logInfo: LoginInfoToBackend = {
			email: UserName,
			password: password,
			twoFactorCode: '',
			twoFactorRecoveryCode: '',
		};
		return this.http
			.post<LoginResponse>(`${this.backendUrl}/login`, logInfo, {
				params: {
					useCookies: useCookie,
					useSessionCookies: useSession,
				},
			})
			.pipe(
				tap((response: any) => {
					// Handle successful login here
					if (
						response.accessToken &&
						response.refreshToken &&
						response.expiresIn
					) {
						this.accessToken = response.accessToken;
						this.refreshToken = response.refreshToken;
						this.tokenExpiration = Date.now() + response.expiresIn;
					} else {
						console.error('Invalid login response:', response);
						throwError(() => new Error('Invalid login response'));
					}
				}),
				catchError((error: any) => {
					// Handle error here
					console.error('Login error:', error);
					return throwError(() => new Error('Login failed'));
				}),
			);
	}

	public FetchURLContent(url: string): Observable<string | ErrorResponse> {
		return this.http.get<string | ErrorResponse>(
			`${this.backendUrl}/${this.SourceFetcherApiUrl}?url=${url}`,
		);
	}

	/**
	 * Fetch an image from a URL as a Blob to handle CORS issues
	 * @param imageUrl The URL of the image to fetch
	 * @returns Observable of the image as a Blob
	 */
	public FetchImageFromURL(imageUrl: string): Observable<Blob> {
		return this.http.get(
			`${this.backendUrl}/${this.SourceFetcherApiUrl}/image?url=${encodeURIComponent(imageUrl)}`,
			{ responseType: 'blob' },
		);
	}
}
